import { Node, nodeInputRule } from '@tiptap/core';
import { PluginKey, Plugin } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { compareVersions } from 'compare-versions';
import EmbedComponent from './EmbedComponent.js';
import { v4 } from 'uuid';
import { toast } from '../../store/alerts.js';

const EmbedPluginKey = new PluginKey("EmbedHandler");
const findNode = (view, nodeId) => {
  let found = null;
  view.state.doc.descendants((node, pos) => {
    if (node.attrs["data-uuid"] === nodeId && node.type.name === "embed") {
      found = {
        node,
        pos
      };
    }
    if (found) return false;
  });
  return found;
};
const handleUpload = async (onUpload, file, view, pos) => {
  if (!onUpload) {
    toast({
      content: "Upload failed."
    });
    return;
  }
  let nodeId = null;
  try {
    const isImage = await new Promise((resolve) => {
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = e.target?.result;
      };
      fileReader.onerror = () => resolve(false);
      fileReader.readAsDataURL(file);
    });
    if (!isImage) return;
    const tempSrc = URL.createObjectURL(file);
    const { schema } = view.state;
    const node = schema.nodes.embed.create({
      mimeType: file.type || "image/*",
      src: tempSrc,
      loading: true,
      alt: file.name,
      "data-uuid": v4()
    });
    nodeId = node.attrs["data-uuid"];
    if (!nodeId) {
      const transaction = view.state.tr.deleteRange(pos, pos + node.nodeSize);
      view.dispatch(transaction);
      return;
    }
    const createTransaction = view.state.tr.insert(pos, node);
    view.dispatch(createTransaction);
    const src = await onUpload(file);
    if (!src) throw new Error("There was an error uploading the file.");
    const foundNode = findNode(view, nodeId);
    if (!foundNode) return;
    const { node: { attrs }, pos: newPos } = foundNode;
    const updateTransaction = view.state.tr.setNodeMarkup(newPos, node.type, {
      ...attrs,
      src,
      loading: false
    }, []);
    view.dispatch(updateTransaction);
  } catch (e) {
    console.error(e);
    toast({
      content: "There was an error uploading the file."
    });
    if (!nodeId) return;
    const foundNode = findNode(view, nodeId);
    if (!foundNode) return;
    const { node, pos: pos2 } = foundNode;
    const transaction = view.state.tr.deleteRange(pos2, pos2 + node.nodeSize);
    view.dispatch(transaction);
  }
};
const imageMarkdownRegex = /(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/;
const version = "0.0.1";
const Embed = Node.create({
  name: "embed",
  group: "inline",
  inline: true,
  selectable: true,
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      loading: {
        default: false,
        parseHTML: (element) => {
          return element.getAttribute("data-loading") === "true";
        },
        renderHTML: (attributes) => {
          if (attributes.loading) {
            return {
              "data-loading": attributes.loading
            };
          }
          return {};
        }
      },
      src: {
        default: null
      },
      alt: {
        default: null
      },
      title: {
        default: null
      },
      width: {
        default: null
      },
      height: {
        default: null
      },
      mimeType: {
        default: null,
        parseHTML: (element) => {
          const mimeType = element.getAttribute("data-mime-type");
          const version2 = element.getAttribute("data-version");
          if (!mimeType && (!version2 || compareVersions(version2, "0.0.2") === -1)) {
            return "image/*";
          }
          return element.getAttribute("data-mime-type");
        },
        renderHTML: (attributes) => {
          return {
            "data-mime-type": attributes.mimeType
          };
        }
      },
      version: {
        default: version,
        parseHTML: (element) => {
          const elementVersion = element.getAttribute("data-version") || version;
          return elementVersion;
        },
        renderHTML: (attributes) => {
          return {
            "data-version": attributes.version
          };
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "img[src]:not(data-loading)"
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const { "data-loading": _dataLoading, ...attributes } = HTMLAttributes;
    return [
      "img",
      attributes
    ];
  },
  addCommands() {
    return {
      setImage: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: {
            ...options,
            mimeType: "image/*"
          }
        });
      },
      uploadImage: (file) => () => {
        const pos = this.editor.state.selection.from;
        const view = this.editor.view;
        const onUpload = EmbedPluginKey.getState(view.state).onUpload;
        if (!onUpload) return false;
        handleUpload(onUpload, file, view, pos);
        return true;
      },
      setOnUpload: (onUpload) => ({ editor }) => {
        const transaction = editor.state.tr.setMeta(EmbedPluginKey, {
          onUpload
        });
        editor.view.dispatch(transaction);
        return true;
      }
    };
  },
  addInputRules() {
    return [
      nodeInputRule({
        find: imageMarkdownRegex,
        type: this.type,
        getAttributes: (match) => {
          const [, , alt, src, title] = match;
          return {
            src,
            alt,
            title
          };
        }
      })
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer((props) => EmbedComponent({
      ...props
    }));
  },
  addProseMirrorPlugins() {
    const { onUpload } = this.storage;
    return [
      new Plugin({
        key: EmbedPluginKey,
        state: {
          init: () => {
            return {
              onUpload
            };
          },
          apply: (tr, pluginState) => {
            const change = tr.getMeta(EmbedPluginKey);
            if (change !== void 0) {
              pluginState = Object.assign({}, pluginState);
              for (const key in change) {
                pluginState[key] = change[key];
              }
            }
            return pluginState;
          }
        },
        props: {
          // since we have no other current drop handlers, we can just return true
          handleDOMEvents: {
            drop: (view, event) => {
              const files = event.dataTransfer?.files;
              if ((files?.length ?? 0) > 1 || (files?.length ?? 0) === 0) {
                return false;
              }
              const file = files?.[0];
              if (!file) return false;
              const onUpload2 = EmbedPluginKey.getState(view.state).onUpload;
              if (!onUpload2) return true;
              event.preventDefault();
              event.stopPropagation();
              const coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY
              });
              if (!coordinates) return true;
              handleUpload(onUpload2, file, view, coordinates.pos);
              return true;
            },
            paste: (view, event) => {
              const files = event.clipboardData?.files;
              if ((files?.length ?? 0) > 1 || (files?.length ?? 0) === 0) {
                return false;
              }
              const file = files?.[0];
              if (!file) return false;
              const onUpload2 = EmbedPluginKey.getState(view.state).onUpload;
              if (!onUpload2) return true;
              event.preventDefault();
              const coordinates = view.posAtCoords(view.coordsAtPos(view.state.selection.from));
              if (!coordinates) return true;
              handleUpload(onUpload2, file, view, coordinates.pos);
              return true;
            }
          }
        }
      })
    ];
  }
});

export { EmbedPluginKey, Embed as default };
//# sourceMappingURL=Embed.js.map
